import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import api from '../../hooks/useApi';
import Modal from '../shared/Modal';
import { monthGrid, shiftMonth, monthLabel, monthOf, dragDates, paintPlan, clearPlan } from '../../utils/standbyCalendar';

// ── The standby calendar: paint standby across months ────────────────────────
// Standby is planned about three months ahead and the Standby row is edited a
// week at a time, so the people who plan it were paging week by week. This
// shows a month of the Standby row; an editor picks a person and a level once
// (the brush), then drags across days to put them on standby. A second brush
// clears. Every stroke is one slot save through the endpoint the cell editor
// uses, so the server's rules apply once: one level a day, a pending record
// per day (approved when the week is confirmed), and the clash check.
//
// A stroke over a day the person already holds in another standby row is a
// clash the server would refuse for the whole stroke, so those days are left
// out and named here, next to the calendar, and the rest still lands.
//
// Viewers and members get the same month read only: no brush, no strokes, and
// their own days in bold, so anyone on the board can see who is covering when.
// On a "current week only" board the weeks they cannot see yet are marked as
// not published, rather than passing for days with nobody on standby.

const TINTS = [
  'bg-blue-50 text-blue-700 border-blue-200',
  'bg-purple-50 text-purple-700 border-purple-200',
  'bg-emerald-50 text-emerald-700 border-emerald-200',
  'bg-amber-50 text-amber-700 border-amber-200',
  'bg-pink-50 text-pink-700 border-pink-200',
  'bg-cyan-50 text-cyan-700 border-cyan-200',
];
const personLabel = (m) => m?.display_label || m?.person_name || '?';
const isWeekend = (iso) => [0, 6].includes(new Date(`${iso}T00:00:00Z`).getUTCDay());
const fmtDay = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

export default function StandbyCalendarModal({ data, initialMonth, currentUserId, onClose }) {
  const plowId = data.plow.id;
  const canEdit = !!data.plow.can_edit;
  const standbySections = useMemo(() => data.sections.filter(s => s.kind === 'standby'), [data.sections]);
  const members = useMemo(() => data.members.filter(m => m.active), [data.members]);
  const levels = data.standby_levels || [];
  const todayISO = new Date().toISOString().slice(0, 10);

  const [month, setMonth] = useState(initialMonth || monthOf(todayISO));
  const [slots, setSlots] = useState([]);
  const [holidays, setHolidays] = useState({});
  const [visibleUntil, setVisibleUntil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(standbySections[0]?.id || '');
  const [personId, setPersonId] = useState('');
  const [levelId, setLevelId] = useState(levels.length === 1 ? levels[0].id : '');
  const [brush, setBrush] = useState('paint');
  const [drag, setDrag] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const dragRef = useRef(null);
  const gridRef = useRef(null);

  const grid = useMemo(() => monthGrid(month), [month]);
  const levelIndex = useMemo(() => new Map(levels.map((l, i) => [l.id, i])), [levels]);
  const memberById = useMemo(() => new Map(members.map(m => [m.person_id, m])), [members]);
  const levelById = useMemo(() => new Map(levels.map(l => [l.id, l])), [levels]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`plows/${plowId}/standby-calendar?from=${grid.from}&to=${grid.to}`);
      setSlots(r.slots || []);
      setHolidays(Object.fromEntries((r.holidays || []).map(h => [h.holiday_date, h.name])));
      setVisibleUntil(r.visible_until || null);
    } catch (e) {
      setMessage({ tone: 'error', text: e.message || 'The calendar could not be loaded' });
    } finally {
      setLoading(false);
    }
  }, [plowId, grid.from, grid.to]);

  useEffect(() => { load(); }, [load]);

  // Painting needs a person and a level; clearing is an eraser and needs neither.
  const ready = canEdit && !!sectionId && (brush === 'clear' || (!!personId && !!levelId));
  const member = memberById.get(personId);
  const level = levelById.get(levelId);

  // ── Strokes ────────────────────────────────────────────────────────────────
  const commit = useCallback(async (d) => {
    if (!d) return;
    const dates = dragDates(d.start, d.current);
    const who = personLabel(member);
    if (brush === 'clear') {
      const plan = clearPlan({ slots, sectionId, dates });
      if (!plan.changes) { setMessage({ tone: 'muted', text: 'Nobody is on standby in this row on those days.' }); return; }
      setSaving(true);
      try {
        for (const st of plan.strokes) {
          await api.put(`plows/${plowId}/slots`, { person_id: st.personId, section_id: sectionId, dates: st.dates, standby_level_id: null });
        }
        const names = plan.strokes.map(st => `${personLabel(memberById.get(st.personId))} (${plural(st.dates.length, 'day')})`);
        setMessage({ tone: 'ok', text: `Taken off standby: ${names.join(', ')}.` });
      } catch (e) {
        setMessage({ tone: 'error', text: e.message || 'Standby could not be cleared' });
      } finally {
        await load();
        setSaving(false);
      }
      return;
    }
    const plan = paintPlan({ slots, sections: standbySections, sectionId, personId, levelId, dates });
    if (!plan.changes) {
      if (plan.clashes.length) {
        setMessage({ tone: 'warn', text: `${who} is already on standby in another row on ${plan.clashes.map(fmtDay).join(', ')}. Nothing changed.` });
      } else {
        setMessage({ tone: 'muted', text: `${who} is already on ${level?.name || 'that level'} on those days.` });
      }
      return;
    }
    setSaving(true);
    try {
      await api.put(`plows/${plowId}/slots`, { person_id: personId, section_id: sectionId, dates: plan.dates, standby_level_id: levelId });
      const parts = [`${who} on ${level?.name || 'standby'} for ${plural(plan.changes, 'day')}, raised as pending`];
      if (plan.replaced.length) parts.push(`${plural(plan.replaced.length, 'day')} moved from another level`);
      if (plan.clashes.length) parts.push(`${plural(plan.clashes.length, 'day')} skipped, already on standby in another row: ${plan.clashes.map(fmtDay).join(', ')}`);
      setMessage({ tone: plan.clashes.length ? 'warn' : 'ok', text: parts.join('; ') + '.' });
      await load();
    } catch (e) {
      setMessage({ tone: 'error', text: e.message || 'Standby could not be saved' });
    } finally {
      setSaving(false);
    }
  }, [brush, slots, standbySections, sectionId, personId, levelId, member, level, plowId, load, memberById]);

  useEffect(() => {
    const up = () => {
      const d = dragRef.current;
      dragRef.current = null;
      setDrag(null);
      if (d) commit(d);
    };
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => { window.removeEventListener('pointerup', up); window.removeEventListener('pointercancel', up); };
  }, [commit]);

  const startDrag = (e, iso) => {
    if (!ready || saving || loading) return;
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    // A touch captures the pointer to the cell it started on, so later cells
    // would never see pointerenter; releasing it keeps the drag moving.
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* not captured */ }
    const d = { start: iso, current: iso };
    dragRef.current = d;
    setDrag(d);
  };
  const extendDrag = (iso) => {
    const d = dragRef.current;
    if (!d || d.current === iso) return;
    const next = { ...d, current: iso };
    dragRef.current = next;
    setDrag(next);
  };
  // Fallback for pointers that do not raise pointerenter on the cell under
  // them (touch on some browsers): read the cell from the point instead.
  const moveDrag = (e) => {
    if (!dragRef.current) return;
    const el = document.elementFromPoint(e.clientX, e.clientY)?.closest?.('[data-date]');
    if (el) extendDrag(el.getAttribute('data-date'));
  };

  const dragging = drag ? new Set(dragDates(drag.start, drag.current)) : null;
  const slotsByDate = useMemo(() => {
    const m = new Map();
    for (const s of slots) {
      if (!m.has(s.slot_date)) m.set(s.slot_date, []);
      m.get(s.slot_date).push(s);
    }
    for (const list of m.values()) {
      list.sort((a, b) => (levelIndex.get(a.level_id) ?? 99) - (levelIndex.get(b.level_id) ?? 99) || personLabel(memberById.get(a.person_id)).localeCompare(personLabel(memberById.get(b.person_id))));
    }
    return m;
  }, [slots, levelIndex, memberById]);

  // Each grid row runs Monday to Sunday, so its first day is the week's Monday.
  const unpublished = useMemo(() => new Set(
    visibleUntil ? grid.weeks.filter(w => w[0] > visibleUntil).flat() : []
  ), [grid, visibleUntil]);

  const monthSlots = slots.filter(s => grid.inMonth(s.slot_date));
  const uncovered = levels.length
    ? grid.weeks.flat().filter(d => grid.inMonth(d) && !unpublished.has(d) && !(slotsByDate.get(d) || []).length).length
    : 0;

  const brushBtn = (key, label) => (
    <button
      type="button"
      aria-pressed={brush === key}
      onClick={() => setBrush(key)}
      className={`px-2.5 py-1 text-[10px] font-semibold rounded border transition-colors
        ${brush === key ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-300 hover:border-navy'}`}
    >
      {label}
    </button>
  );

  const tone = { ok: 'text-emerald-700', warn: 'text-amber-700', error: 'text-red-600', muted: 'text-gray-500' };

  return (
    <Modal
      title={`Standby calendar · ${data.plow.name}`}
      onClose={onClose}
      xl
      footer={<>
        <p className="mr-auto text-[9px] text-gray-400">
          {loading ? 'Loading…' : `${plural(monthSlots.length, 'standby day')} in ${monthLabel(month)}${uncovered ? `, ${plural(uncovered, 'day')} with nobody on standby` : ''}. Pending days are approved when their week is confirmed.`}
        </p>
        <button type="button" className="px-4 py-1.5 text-[10px] font-bold text-white bg-navy rounded hover:opacity-90" onClick={onClose}>{canEdit ? 'Done' : 'Close'}</button>
      </>}
    >
      <div className="space-y-3">
        {!canEdit && (
          <p className="text-[10px] text-gray-500">
            Who is on standby on this board, a month at a time. You are {data.plow.my_level === 'viewer' ? 'a viewer' : 'on this PLOW'}, so the calendar is read only; its editors plan it.
          </p>
        )}

        {/* The brush: who, which level, and paint or clear. Set once, then drag. */}
        {canEdit && (
        <div className="flex flex-wrap items-end gap-3">
          <div className="basis-48 flex-none">
            <label className="form-label">Who</label>
            <select className="form-select" value={personId} onChange={e => { setPersonId(e.target.value); setMessage(null); }} autoFocus disabled={brush === 'clear'}>
              <option value="">Pick a person…</option>
              {members.map(m => <option key={m.person_id} value={m.person_id}>{personLabel(m)}</option>)}
            </select>
          </div>
          <div className="basis-44 flex-none">
            <label className="form-label">Standby level</label>
            <select className="form-select" value={levelId} onChange={e => { setLevelId(e.target.value); setMessage(null); }} disabled={brush === 'clear'}>
              <option value="">Pick a level…</option>
              {levels.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          {standbySections.length > 1 && (
            <div className="basis-40 flex-none">
              <label className="form-label">Row</label>
              <select className="form-select" value={sectionId} onChange={e => setSectionId(e.target.value)}>
                {standbySections.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="form-label">Brush</label>
            <div className="flex gap-1.5">
              {brushBtn('paint', 'Put on standby')}
              {brushBtn('clear', 'Clear (eraser)')}
            </div>
          </div>
          <p className="text-[9px] text-gray-400 self-center ml-auto max-w-[260px]">
            {brush === 'clear'
              ? 'Drag across days to take whoever is on standby off this row.'
              : ready
                ? <>Drag across days to put {personLabel(member)} on {level?.name}. Each stroke saves as you go.</>
                : 'Pick a person and a level, then drag across days on the calendar.'}
          </p>
        </div>
        )}

        {/* Month navigation */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <button type="button" className="px-2 py-1 text-[10px] font-semibold text-navy border border-gray-300 rounded hover:border-navy" onClick={() => setMonth(m => shiftMonth(m, -1))} aria-label="Previous month">‹</button>
            <button type="button" className="px-2 py-1 text-[10px] font-semibold text-navy border border-gray-300 rounded hover:border-navy" onClick={() => setMonth(monthOf(todayISO))}>This month</button>
            <button type="button" className="px-2 py-1 text-[10px] font-semibold text-navy border border-gray-300 rounded hover:border-navy" onClick={() => setMonth(m => shiftMonth(m, 1))} aria-label="Next month">›</button>
          </div>
          <h3 className="text-sm font-bold text-navy">{monthLabel(month)}</h3>
          <div className="flex flex-wrap gap-1.5 justify-end">
            {levels.map((l, i) => (
              <span key={l.id} className={`px-1.5 py-0.5 rounded border text-[9px] font-semibold ${TINTS[i % TINTS.length]}`}>{l.name}</span>
            ))}
          </div>
        </div>

        {/* What the last stroke did, above the grid so it is in view whatever the month's height. */}
        <div className="min-h-[16px]">
          {message && <p className={`text-[10px] font-semibold ${tone[message.tone] || tone.muted}`}>{message.text}</p>}
          {!message && (
            <p className="text-[9px] text-gray-400">
              Dashed chips are pending; solid chips are approved. {canEdit ? 'Bold chips are the person you picked.' : 'Your own days are in bold.'}
              {visibleUntil && ' Only editors see beyond the weeks that are published: next week opens to you on Friday.'}
            </p>
          )}
        </div>

        {/* The grid. Cells are pointer targets; the stroke is committed on pointerup anywhere. */}
        <div
          ref={gridRef}
          className={`select-none ${ready && !saving ? 'cursor-crosshair' : ''} ${saving || loading ? 'opacity-60' : ''}`}
          style={canEdit ? { touchAction: 'none' } : undefined}
          onPointerMove={canEdit ? moveDrag : undefined}
        >
          <div className="grid grid-cols-7 gap-px text-center text-[9px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => <div key={d}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-px bg-gray-200 border border-gray-200 rounded overflow-hidden">
            {grid.weeks.flat().map(iso => {
              const inMonth = grid.inMonth(iso);
              const day = slotsByDate.get(iso) || [];
              const inStroke = dragging?.has(iso);
              const holiday = holidays[iso];
              const hidden = inMonth && unpublished.has(iso);
              return (
                <div
                  key={iso}
                  data-date={iso}
                  role="gridcell"
                  aria-label={fmtDay(iso)}
                  onPointerDown={canEdit ? (e) => startDrag(e, iso) : undefined}
                  onPointerEnter={canEdit ? () => extendDrag(iso) : undefined}
                  className={`min-h-[72px] min-w-0 p-1 flex flex-col gap-0.5 transition-colors
                    ${inMonth ? (hidden || isWeekend(iso) || holiday ? 'bg-gray-50' : 'bg-white') : 'bg-gray-100 text-gray-300'}
                    ${inStroke ? (brush === 'paint' ? 'ring-2 ring-inset ring-navy bg-blue-50' : 'ring-2 ring-inset ring-red-400 bg-red-50') : ''}
                    ${iso === todayISO ? 'outline outline-1 outline-gold -outline-offset-1' : ''}`}
                >
                  <div className="flex items-baseline justify-between gap-1">
                    <span className={`text-[10px] font-semibold ${inMonth ? 'text-gray-700' : 'text-gray-300'}`}>{Number(iso.slice(8))}</span>
                    {holiday && <span className="text-[8px] text-gold font-semibold truncate" title={holiday}>Bank hol</span>}
                  </div>
                  {hidden && <span className="text-[8px] text-gray-400 leading-tight" title="Only editors see beyond the current week on this PLOW">Not published yet</span>}
                  {day.map(s => {
                    const mine = s.person_id === (canEdit ? personId : currentUserId);
                    const inRow = s.section_id === sectionId;
                    return (
                      <span
                        key={`${s.section_id}-${s.person_id}`}
                        title={`${personLabel(memberById.get(s.person_id))} · ${levelById.get(s.level_id)?.name || 'Standby'}${s.standby_status ? ` · ${s.standby_status}` : ''}${s.note ? ` · ${s.note}` : ''}`}
                        className={`block truncate rounded border px-1 text-[9px] leading-4
                          ${TINTS[(levelIndex.get(s.level_id) ?? 0) % TINTS.length]}
                          ${s.standby_status === 'approved' ? '' : 'border-dashed'}
                          ${mine ? 'font-bold' : 'font-medium'}
                          ${!inRow && standbySections.length > 1 ? 'opacity-60' : ''}`}
                      >
                        {personLabel(memberById.get(s.person_id))}
                      </span>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </Modal>
  );
}
